import type { CSSProperties } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { useFleetSummary } from '@/hooks/use-fleet-summary';

interface KpiItem {
  label: string;
  value: string;
  dot: string;
  valueClass: string;
}

export function KpiStrip(): JSX.Element {
  const { data, isLoading } = useFleetSummary();

  if (isLoading || !data) {
    return <Skeleton className="h-9 w-full" />;
  }

  const { by_status: byStatus } = data.counts;
  const { fresh, total } = data.heartbeats;

  const items: KpiItem[] = [
    {
      label: 'healthy',
      value: `${fresh}/${total}`,
      dot: 'var(--status-active)',
      valueClass: 'text-status-active',
    },
    {
      label: 'quarantine',
      value: String(byStatus['quarantined'] ?? 0),
      dot: 'var(--status-degraded)',
      valueClass: 'text-status-degraded',
    },
    {
      label: 'in-flight',
      value: String(byStatus['provisioning'] ?? 0),
      dot: 'var(--status-inflight)',
      valueClass: 'text-status-inflight',
    },
    {
      label: 'decommissioned',
      value: String(byStatus['decommissioned'] ?? 0),
      dot: 'var(--status-idle)',
      valueClass: 'text-status-idle',
    },
  ];

  return (
    <div className="flex flex-wrap items-stretch divide-x divide-border overflow-hidden rounded-md border border-border bg-card shadow-panel">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-2.5 px-4 py-2.5">
          <span
            className="signal-dot"
            style={{ ['--bg-dot']: item.dot, backgroundColor: item.dot } as CSSProperties}
            aria-hidden
          />
          <span className={`font-mono text-base font-semibold tabular-nums ${item.valueClass}`}>
            {item.value}
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
}
